/**
 * The staff artwork screens, headless (docs/features/order-to-invoice/
 * 66_views_and_frontend_artwork_update_spec.md §2–§3): where each version
 * stands as a rail node, its one next step and the rest, the parts it could
 * share, and what an act-for approval must tick.
 *
 * Every sentence is the backend's (`status_text`, a finding's `sentence`, a
 * proof's `status_line`); this only places those facts. What is here is the
 * choice of node colour per stage and the buttons' own labels. The stage
 * derivation itself is views' (doc 66 §6.3): the page never recomputes it
 * from files and proofs.
 *
 * - One next step per version (*Upload*, *Upload a new file*, *Check
 *   warnings*, *Accept for {customer}*, *Contact*); the rest sit in the row's
 *   ⋯. A step shows only where the read carries its URL.
 * - Never a bare "v": "Rev 2", "Proof 2".
 */

import type {
  ArtworkStage,
  StaffArtworkLine,
  StaffFinding,
  StaffVersionArtwork,
} from "../data/artwork-staff-types";
import type { ActionButton } from "../data/order-to-invoice-types";
import type { StepProblem, StepState } from "./lifecycle-rail";

// ── Where a version stands, as a node ────────────────────────────────────

export type ArtworkTone = "neutral" | "at_risk" | "late" | "done";

/**
 * A stage's tone. A file that can't print is red: nothing can move until a
 * new one arrives. A held check and a proof sent back are amber: a person has
 * something to do. Approved is done.
 */
export function stageTone(stage: ArtworkStage): ArtworkTone {
  switch (stage) {
    case "cant_print":
      return "late";
    case "held":
    case "changes_asked":
      return "at_risk";
    case "approved":
      return "done";
    default:
      return "neutral";
  }
}

/** The rail node of a version: to do until a file is in, then in progress, done when approved. */
export function stageNode(stage: ArtworkStage): {
  state: StepState;
  problem: StepProblem | null;
} {
  const tone = stageTone(stage);
  if (tone === "done") return { state: "done", problem: null };
  if (tone === "late" || tone === "at_risk") {
    return { state: "in_progress", problem: tone };
  }
  return {
    state: stage === "files_owed" ? "to_do" : "in_progress",
    problem: null,
  };
}

// ── The steps ─────────────────────────────────────────────────────────────

export type ArtworkStep =
  | "tag_pages"
  | "upload"
  | "upload_new"
  | "replace_part"
  | "check_warnings"
  | "accept_without_proof"
  | "contact"
  | "decide_for_customer"
  | "resend_proof"
  | "reopen";

/** A held finding the version offers a decision for. */
export function heldFindings(version: StaffVersionArtwork): StaffFinding[] {
  return version.parts
    .flatMap((p) => p.findings)
    .filter((f) => f.tier === "unrecognised" && f.decide_url !== null);
}

/** Whether any part accepts a file now. */
function acceptsFile(version: StaffVersionArtwork): boolean {
  return version.parts.some((p) => p.upload_url !== null);
}

/** Whether a part already has a file a replacement would supersede. */
function canReplacePart(version: StaffVersionArtwork): boolean {
  return version.parts.some((p) => p.upload_url !== null && p.file !== null);
}

/** The row's one next step; null when there is none to take (or the read offers none). */
export function nextStepOf(
  version: StaffVersionArtwork,
  hasContact: boolean,
): ArtworkStep | null {
  // One file for every part waits for its page tags (doc 87): that is the next thing, whatever else the stage offers.
  if (version.pending_combined_file) return "tag_pages";
  switch (version.stage) {
    case "files_owed":
      return acceptsFile(version) ? "upload" : null;
    case "cant_print":
    case "changes_asked":
      return acceptsFile(version) ? "upload_new" : null;
    case "held":
      return heldFindings(version).length > 0 ? "check_warnings" : null;
    case "checked":
      return version.accept_without_proof_url ? "accept_without_proof" : null;
    case "proof_out":
      return hasContact ? "contact" : null;
    case "checking":
    case "tool_failed":
    case "approved":
      return null;
  }
}

/** The row's other steps, for its ⋯. Downloads sit beside each file. */
export function moreStepsOf(
  version: StaffVersionArtwork,
  next: ArtworkStep | null,
): ArtworkStep[] {
  const proof = version.current_proof;
  const steps: ArtworkStep[] = [];
  if (version.stage === "proof_out" && proof) {
    if (proof.decide_url) steps.push("decide_for_customer");
    if (proof.email_now_url) steps.push("resend_proof");
  }
  if (version.stage === "approved" && proof?.reopen_url) steps.push("reopen");
  if (canReplacePart(version)) steps.push("replace_part");
  return steps.filter((s) => s !== next);
}

/** The button's label. A decision is "for {customer}": it is the customer's to make. */
export function stepLabel(
  step: ArtworkStep,
  version: StaffVersionArtwork,
  party: string,
): string {
  switch (step) {
    case "tag_pages":
      return "Which pages are which?";
    case "upload":
      return "Upload";
    case "upload_new":
      return "Upload a new file";
    case "replace_part":
      return version.parts.filter((p) => p.needs_file).length <= 1
        ? "Replace the file"
        : "Replace a part";
    case "check_warnings":
      return "Check warnings…";
    case "accept_without_proof":
      return `Accept for ${party}…`;
    case "contact":
      return "Contact…";
    case "decide_for_customer":
      return `Decide for ${party}…`;
    case "resend_proof":
      return "Resend proof";
    case "reopen":
      return "Reopen…";
  }
}

// ── Sharing a part ───────────────────────────────────────────────────────

export interface ShareOffer {
  partKey: string;
  fromVersionId: string;
  /** "Bristol": the version the file is shared from. */
  fromName: string;
  fileName: string;
}

/**
 * For a part with no file yet: the same part of another version of the line
 * that already has one, which it can share instead of supplying it again (one
 * cover for three cafés). Offered only where the read carries the part's
 * `share_url`.
 */
export function shareOffers(
  line: StaffArtworkLine,
  version: StaffVersionArtwork,
): ShareOffer[] {
  return version.parts.flatMap((part) => {
    if (part.file !== null || part.share_url === null) return [];
    for (const other of line.versions) {
      if (other.version_id === version.version_id) continue;
      const file = other.parts.find((p) => p.part_key === part.part_key)?.file;
      if (file) {
        return [
          {
            partKey: part.part_key,
            fromVersionId: other.version_id,
            fromName:
              other.name ?? other.label ?? other.display ?? "another version",
            fileName: file.file_name,
          },
        ];
      }
    }
    return [];
  });
}

// ── Deciding for the customer ────────────────────────────────────────────

/** The findings an approval must tick: every worth-knowing one on the proof's revision (doc 62 §5.4). */
export function worthKnowing(findings: StaffFinding[]): StaffFinding[] {
  return findings.filter((f) => f.tier === "worth_knowing");
}

// ── The line's name for a dialog or a button ─────────────────────────────

/** ".01 A Bristol", or ".02 Pull-up banner" for a line with a single version. */
export function versionTitle(
  line: Pick<StaffArtworkLine, "line_no">,
  version: Pick<StaffVersionArtwork, "display">,
  lineName: string,
): string {
  return `${line.line_no} ${version.display ?? lineName}`;
}

// ── The line chain's Artwork cell ────────────────────────────────────────

/**
 * A line's artwork in its chain: the read's milestone for the line, as the
 * other steps' are ("1 of 2 approved", "can't print"); null for a line that
 * needs none.
 */
export function lineArtworkCell(
  line: StaffArtworkLine,
): { status: string; tone: ArtworkTone } | null {
  const m = line.milestone;
  if (line.kind !== "artwork" || !m) return null;
  const tone: ArtworkTone =
    m.state === "done"
      ? "done"
      : m.is_late
        ? "late"
        : m.state === "attention"
          ? "at_risk"
          : "neutral";
  return {
    status: [m.phrase, m.note].filter(Boolean).join(" · ") || "—",
    tone,
  };
}

// ── The action box's artwork buttons ─────────────────────────────────────

/** An action-box row's button for an artwork step (doc 66 §2.2), with the version it is about. */
export type ArtworkRowButton = ActionButton & {
  kind:
    | "decide_finding"
    | "upload_artwork"
    | "accept_without_proof"
    | "decide_proof";
  version_id: string;
};

const ARTWORK_ROW_BUTTON_KINDS: ReadonlySet<ActionButton["kind"]> = new Set<
  ArtworkRowButton["kind"]
>(["decide_finding", "upload_artwork", "accept_without_proof", "decide_proof"]);

/** The button as an artwork step's, or null for any other row's (the box's own). */
export function artworkRowButton(
  button: ActionButton,
): ArtworkRowButton | null {
  return ARTWORK_ROW_BUTTON_KINDS.has(button.kind) && button.version_id
    ? (button as ArtworkRowButton)
    : null;
}
